import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Sidebar } from './Sidebar';

export type ScrollMode = 'document' | 'workspace';

export interface LayoutProps {
  children: ReactNode;
  scrollMode?: ScrollMode;
}

const ROUTE_LAYOUT_MANIFEST: ReadonlyArray<{
  matches: (pathname: string) => boolean;
  scrollMode: ScrollMode;
}> = [
  {
    matches: (pathname) => pathname === '/inbox' || pathname.startsWith('/inbox/'),
    scrollMode: 'workspace',
  },
];

export function resolveRouteScrollMode(pathname: string): ScrollMode {
  return ROUTE_LAYOUT_MANIFEST.find((entry) => entry.matches(pathname))?.scrollMode ?? 'document';
}

export function Layout({ children, scrollMode }: LayoutProps) {
  const { pathname } = useLocation();
  const resolvedScrollMode = scrollMode ?? resolveRouteScrollMode(pathname);

  return (
    <div className="flex h-dvh min-h-dvh flex-col overflow-hidden bg-background text-foreground md:flex-row">
      <a
        href="#main-content"
        className="fixed left-3 top-3 z-[100] -translate-y-20 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-elevation-3 transition-transform focus:translate-y-0"
      >
        Skip to main content
      </a>
      <Sidebar />
      <main
        id="main-content"
        tabIndex={-1}
        data-scroll-mode={resolvedScrollMode}
        className={cn(
          'flex min-h-0 flex-1 flex-col bg-background pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0',
          resolvedScrollMode === 'workspace'
            ? 'overflow-hidden'
            : 'overflow-y-auto [&>.overflow-y-auto]:overflow-visible'
        )}
      >
        {children}
      </main>
    </div>
  );
}
